/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
import { CheckedItemsInfo } from "../../_helpers/list-selector/list-selector";

/* CUSTOM IMPORTS */
import { config } from "../../../common/config";
import { Locale } from "../../../common/locale";
import { renderListItems } from "../../../common/render-list-item";
import { ObjectData, objectsToListItemData } from "./helpers";

@Component({
  tag: "gx-ide-bpm-objects-selector",
  styleUrl: "bpm-objects-selector.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-objects-selector"]
})
export class GxIdeBpmExportXpdl {
  // 1.OWN PROPERTIES //

  /**
   * The component hard-coded strings translations.
   */
  private _componentLocale: any;
  private renderedFirstTime = false;

  private includeAll = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeBpmObjectsSelectorElement;
  private includeAllCheckboxEl: HTMLChCheckboxElement;

  // 3.STATE() VARIABLES //

  @State() objects: ObjectData[] = [];

  /**
   * Contains the list of selected object ids
   */
  @State() selectedObjects: string[] = [];

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   *The current selection in the list of objects
   */
  @Prop({ mutable: true }) selection: SelectionData;

  /**
   * Callback invoked when the user presses the 'Add' button.
   * This function is expected to return a list of selected objects' basic information.
   * @returns {Promise<ObjectData[]>} A Promise that resolves to an array of ObjectData. Each ObjectData contains the ID, icon, and name of an object.
   */
  @Prop() readonly addCallback: () => Promise<ObjectData[]>;

  /**
   * Callback invoked when the user presses the 'References' button.
   * Receives a parameter which is an array of IDs of the objects currently added to the list.
   * Returns a Promise that resolves to an array of ObjectData, where each ObjectData contains the ID, icon, and name of an object.
   * @param {string[]} ids - An array of IDs of the objects currently added to the list.
   * @returns {Promise<ObjectData[]>} A Promise that resolves to an array of ObjectData. Each ObjectData contains the ID, icon, and name of an object.
   */
  @Prop() readonly referencesCallback: (ids: string[]) => Promise<ObjectData[]>;

  // 5.EVENTS (EMIT) //

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event()
  componentDidRenderFirstTime: EventEmitter<HTMLGxIdeBpmObjectsSelectorElement>;

  /**
   * @description Gets fired when the objects selection list change or when 'Select All' checkbox state changes.
   */
  @Event() selectionChanged: EventEmitter<string>;

  // 6.COMPONENT LIFECYCLE METHODS //

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidRender() {
    if (!this.renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(this.el);
      this.renderedFirstTime = true;
    }
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private objectSelectionChangedHandler = (
    selectedItem: CustomEvent<CheckedItemsInfo>
  ) => {
    const selectedObjects = Array.isArray(selectedItem.detail)
      ? selectedItem.detail
      : [selectedItem.detail];

    this.selectedObjects = selectedObjects.map(item => item.itemId);
  };

  private removeObjectsHandler = () => {
    this.objects = this.objects.filter(
      object => !this.selectedObjects.includes(object.id)
    );

    this.selectedObjects = [];
    this.updateSelection(false);
  };

  private removeAllObjectsHandler = () => {
    this.objects = [];
    this.selectedObjects = [];
    this.updateSelection(false);
  };

  private includeAllHandler = () => {
    this.includeAll = this.includeAllCheckboxEl.value === "true";
    if (this.includeAll) {
      this.selectedObjects = this.objects.map(object => object.id);
      this.objects = [];
      this.updateSelection(true);
    } else {
      this.selectedObjects = [];
      this.updateSelection(false);
    }

    this.selectionChanged.emit();
  };
  private addCallbackHandler = async () => {
    if (this.addCallback) {
      const resultObjects = await this.addCallback();
      const newObjects = resultObjects.filter(
        resultObj =>
          !this.objects.some(existingObj => existingObj.id === resultObj.id)
      );
      this.objects = [...this.objects, ...newObjects];
      this.updateSelection(false);
    }
  };

  private referencesCallbackHandler = async () => {
    if (this.referencesCallback && this.objects.length > 0) {
      const ids = this.objects.map(obj => obj.id);

      const resultObjects = await this.referencesCallback(ids);

      const newObjects = resultObjects.filter(
        resultObj =>
          !this.objects.some(existingObj => existingObj.id === resultObj.id)
      );

      this.objects = [...this.objects, ...newObjects];
      this.updateSelection(false);
    }
  };

  private updateSelection = (includeAll: boolean) => {
    const data: SelectionData = {
      includeAll: this.includeAllCheckboxEl.value === "true",
      objectIds: includeAll
        ? this.selectedObjects
        : this.objects.map(object => object.id)
    };

    this.selection = data;
    this.selectionChanged.emit();
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host class="gx-ide-component gx-ide-bpm-objects-selector">
        <div class="gx-ide-main-wrapper">
          <gx-ide-container slimmerFooter={config.gxIdeContainer.slimmerFooter}>
            <gx-ide-list-selector
              id="path-list"
              type="multi-selection"
              listName="path-list"
              ordered
              onSelectionChanged={this.objectSelectionChangedHandler}
              class="path-list"
            >
              {renderListItems(objectsToListItemData(this.objects))}
            </gx-ide-list-selector>

            <div class="controls-wrapper">
              <label htmlFor="include-all">
                {this._componentLocale.main.includeAllLabel}
              </label>
              <ch-checkbox
                id="include-all"
                accessibleName={this._componentLocale.main.includeAllLabel}
                checkedValue="true"
                unCheckedValue="false"
                value="checked"
                onInput={this.includeAllHandler}
                ref={(el: HTMLChCheckboxElement) =>
                  (this.includeAllCheckboxEl = el as HTMLChCheckboxElement)
                }
              ></ch-checkbox>
              <button
                onClick={this.addCallbackHandler}
                disabled={this.includeAll}
              >
                {this._componentLocale.main.addButton}
              </button>
              <button
                onClick={this.referencesCallbackHandler}
                disabled={this.objects.length === 0}
              >
                {this._componentLocale.main.referencesButton}
              </button>
              <button
                onClick={this.removeObjectsHandler}
                disabled={this.selectedObjects.length === 0 || this.includeAll}
              >
                {this._componentLocale.main.removeButton} (
                {this.selectedObjects.length})
              </button>
            </div>

            <button
              slot="footer-end"
              onClick={this.removeAllObjectsHandler}
              disabled={this.includeAll}
            >
              {this._componentLocale.main.clearButton}
            </button>
          </gx-ide-container>
        </div>
      </Host>
    );
  }
}

export type SelectionData = {
  includeAll: boolean;
  objectIds: string[];
};
